import React, { useState, useEffect, useRef } from 'react';
import SelectField from '../components/SelectField';
import { FaArrowDown, FaArrowUp, FaExclamationTriangle, FaTimes, FaUpload } from 'react-icons/fa';
import FileUpload from '../components/FileUpload';
import { InlineSpinner } from '../components/LoadingState';
import { useNavigate } from 'react-router-dom';
import { uploadScorecardFile, listCampaignsApi, getAllTemplates, createTemplateApi } from '../api/api';
import ConfirmationModal from '../components/ConfirmationModal';
import { moveItem, withClientKey, withClientKeys, withSortOrder } from '../common/questionOrder';
import {
  ASSESSMENT_TYPE,
  ASSESSMENT_TYPE_OPTIONS,
  SCORING_SYSTEM,
  asComplianceQuestion,
} from '../common/assessment';

// An uploaded scorecard is scored yes/no; it can instead be made compliance-only, where every
// question is a pass/fail Compliance check and there is no QA score. (Legacy numeric scoring is
// chosen in the editor, not here.)
const UPLOAD_SCORING_OPTIONS = [
  {
    value: SCORING_SYSTEM.YES_NO,
    label: 'Yes/No Scoring',
    description: 'Quality, Compliance and Conduct questions; Quality questions are weighted',
  },
  {
    value: SCORING_SYSTEM.COMPLIANCE,
    label: 'Compliance-only',
    description: 'Every question is a pass/fail compliance check - no QA score',
  },
];

const TemplateUpload = ({ setAlert }) => {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  // State management
  const [file, setFile] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [campaign, setCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [scoringSystem, setScoringSystem] = useState(SCORING_SYSTEM.YES_NO);
  const [confirmComplianceOnly, setConfirmComplianceOnly] = useState(false);
  const complianceOnly = scoringSystem === SCORING_SYSTEM.COMPLIANCE;
  const [previewMode, setPreviewMode] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load campaigns and templates on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [campaignsRes, templatesRes] = await Promise.all([
          listCampaignsApi(),
          getAllTemplates()
        ]);
        if (isMountedRef.current) {
          setCampaigns(campaignsRes.campaigns || []);
          setTemplates(templatesRes.templates || []);
        }
      } catch (err) {
        console.error('Failed to load data', err);
      }
    };
    loadData();
  }, []);

  // File upload handler with validation
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg',
      'image/gif',
      'image/webp'
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setAlert?.({
        variant: 'error',
        heading: 'Invalid File Type',
        text: 'Please upload PDF, Excel, CSV, Word, or image files only.'
      });
      return;
    }

    // Validate file size (max 32MB for PDFs, 10MB for others)
    const maxSize = selectedFile.type === 'application/pdf' ? 32 * 1024 * 1024 : 10 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setAlert?.({
        variant: 'error',
        heading: 'File Too Large',
        text: `Maximum file size is ${maxSize / 1024 / 1024}MB for ${selectedFile.type.includes('pdf') ? 'PDFs' : 'this file type'}.`
      });
      return;
    }

    setFile(selectedFile);

    // Auto-populate template name from filename
    if (!templateName) {
      const name = selectedFile.name
        .replace(/\.[^/.]+$/, '')
        .replace(/[^a-zA-Z0-9-]/g, '-')
        .toLowerCase();
      setTemplateName(name);
    }
  };

  // Check for duplicate template name
  const isDuplicateName = (name) => {
    return templates.some(t => t.templateName === name);
  };

  // Upload and extract
  const handleUpload = async () => {
    if (!file || !templateName.trim()) {
      setAlert?.({
        variant: 'error',
        heading: 'Missing Information',
        text: 'Please select a file and provide a template name.'
      });
      return;
    }

    if (isDuplicateName(templateName)) {
      setAlert?.({
        variant: 'error',
        heading: 'Duplicate Name',
        text: 'A template with this name already exists. Please choose a different name.'
      });
      return;
    }

    setUploading(true);
    setExtracting(true);

    try {
      // Upload to S3 and trigger extraction
      const result = await uploadScorecardFile(file, templateName, campaign);

      // Only update state if component is still mounted
      if (!isMountedRef.current) return;

      setExtractedData({
        ...result.extracted_data,
        questions: withClientKeys(result.extracted_data?.questions || []),
      });
      setValidationResults(result.validation_results);

      // Move to preview mode (useEffect will auto-open modal)
      setPreviewMode(true);

      // Check validation results for alerts
      if (!result.validation_results.is_valid) {
        setAlert?.({
          variant: 'warning',
          heading: 'Validation Issues',
          text: `Extraction completed with ${result.validation_results.errors.length} errors. Please review the validation summary below.`
        });
      } else if (result.validation_results.warnings.length > 0) {
        setAlert?.({
          variant: 'info',
          heading: 'Review Recommended',
          text: `Extraction completed with ${result.validation_results.warnings.length} warnings. Scroll down to review the validation summary.`
        });
      } else {
        setAlert?.({
          variant: 'success',
          heading: 'Extraction Successful',
          text: `Successfully extracted ${result.extracted_data.questions.length} questions with ${(result.validation_results.confidence_score * 100).toFixed(0)}% confidence.`
        });
      }

    } catch (err) {
      console.error('Upload failed:', err);
      if (!isMountedRef.current) return;

      // Check if this is a gateway timeout error (504)
      const errorMsg = err.message || '';
      const isGatewayTimeout = errorMsg.includes('504');

      if (isGatewayTimeout) {
        // For timeouts, show a processing message instead of an error
        setAlert?.({
          variant: 'info',
          heading: 'Processing in Progress',
          text: 'Your scorecard is being processed in the background. This may take a few minutes. Please check back shortly or refresh the templates list.'
        });
      } else {
        // For other errors, show the error message
        setAlert?.({
          variant: 'error',
          heading: 'Upload Failed',
          text: errorMsg || 'Failed to process scorecard file.'
        });
      }
    } finally {
      if (isMountedRef.current) {
        setUploading(false);
        setExtracting(false);
      }
    }
  };

  // Save to DynamoDB (persists the edited questions before navigating)
  const handleSave = async () => {
    setSaving(true);
    try {
      await createTemplateApi({
        templateName,
        campaign,
        scoringSystem,
        questions: withSortOrder(extractedData?.questions || [])
      });

      if (!isMountedRef.current) return;

      setAlert?.({
        variant: 'success',
        heading: 'Template Saved',
        text: 'Scorecard template has been saved successfully. Redirecting to editor...'
      });
      // Redirect to the template editor view
      navigate(`/templates/${encodeURIComponent(templateName)}`);
    } catch (err) {
      console.error('Save failed:', err);
      if (!isMountedRef.current) return;
      setAlert?.({
        variant: 'error',
        heading: 'Save Failed',
        text: err.message || 'Failed to save scorecard template. Please try again.'
      });
    } finally {
      if (isMountedRef.current) {
        setSaving(false);
      }
    }
  };

  // Edit question in preview
  const updateQuestion = (index, key, value) => {
    setExtractedData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === index ? { ...q, [key]: value } : q
      )
    }));
  };

  // Remove question
  const removeQuestion = (index) => {
    setExtractedData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  // Move a question up (-1) or down (+1). The order on screen is the order saved.
  const moveQuestion = (index, offset) => {
    setExtractedData(prev => ({
      ...prev,
      questions: moveItem(prev.questions, index, offset)
    }));
  };

  // Add question. Answered yes/no like the extracted ones, so a hand-added question is not left
  // with no response type (which the scorer would run through the legacy prompt).
  const addQuestion = () => {
    setExtractedData(prev => ({
      ...prev,
      questions: [...prev.questions, withClientKey(asComplianceQuestion({
        questionName: `Q${prev.questions.length + 1}`,
        description: '',
        weight: 0,
        expected: '',
        success: 'yes'
      }))]
    }));
  };

  const nonComplianceCount = (extractedData?.questions || [])
    .filter(q => q.type !== ASSESSMENT_TYPE.COMPLIANCE).length;

  const applyComplianceOnly = () => {
    setScoringSystem(SCORING_SYSTEM.COMPLIANCE);
    setExtractedData(prev => prev && ({ ...prev, questions: prev.questions.map(asComplianceQuestion) }));
  };

  // Switching to Compliance-only converts the other question types, so it is confirmed first.
  const chooseScoringSystem = (next) => {
    if (next === scoringSystem) return;
    if (next === SCORING_SYSTEM.COMPLIANCE) {
      if (nonComplianceCount > 0) setConfirmComplianceOnly(true);
      else applyComplianceOnly();
      return;
    }
    setScoringSystem(next);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Upload Scorecard</h1>
        <p className="text-gray-500 text-sm mt-1">
          Upload a scorecard document — the questions are extracted automatically for you to review before saving.
        </p>
      </div>

      {!previewMode ? (
        // Upload Form
        <div className="card space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Scorecard File *
            </label>
            <FileUpload
              value={file ? [file] : []}
              accept=".pdf,.xlsx,.xls,.csv,.docx,.png,.jpg,.jpeg,.gif,.webp"
              buttonText="Choose scorecard file"
              hint="Supported: PDF (32MB) • Excel/CSV (10MB) • Word (10MB) • Images (5MB)"
              onChange={({ detail }) => {
                if (detail.value.length === 0) {
                  setFile(null);
                  return;
                }
                // Reuse the existing validation path untouched.
                handleFileChange({ target: { files: detail.value } });
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Template Name *
            </label>
            <input
              className={`input-field ${isDuplicateName(templateName) ? 'border-red-500' : ''}`}
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              placeholder="e.g., customer-service-v1"
            />
            {isDuplicateName(templateName) && (
              <p className="flex items-center gap-1.5 text-sm text-red-700 mt-1">
                <FaExclamationTriangle className="flex-shrink-0 text-xs" /> Template name already exists
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1.5">
              Campaign (optional)
            </label>
            <SelectField
              value={campaign}
              onChange={e => setCampaign(e.target.value)}
              className="select-field"
            >
              <option value="">None</option>
              {campaigns.map(c => (
                <option key={c.campaignName} value={c.campaignName}>
                  {c.campaignName}
                </option>
              ))}
            </SelectField>
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-4">
            <button
              onClick={() => navigate('/templates')}
              className="btn-outline"
            >
              <FaTimes />
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={!file || !templateName.trim() || uploading || isDuplicateName(templateName)}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <><FaUpload /> {uploading ? 'Processing...' : 'Upload & Extract'}</>
            </button>
          </div>

          {extracting && (
            <div className="bg-cloudza-50 border border-cloudza-100 rounded-xl p-4 mt-4">
              <div className="flex items-center space-x-3">
                <InlineSpinner className="!h-5 !w-5" />
                <div>
                  <p className="text-cloudza-800 font-medium">
                    AI is analyzing your scorecard...
                  </p>
                  <p className="text-cloudza-600 text-sm">
                    This may take 5-10 seconds. Using Bedrock with native document understanding.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Preview & Edit Extracted Data
        <div className="space-y-4">
          {/* Validation Summary */}
          {validationResults && (
            <div className={`rounded-lg border-2 p-4 ${
              !validationResults.is_valid
                ? 'bg-red-50 border-red-300'
                : validationResults.warnings && validationResults.warnings.length > 0
                ? 'bg-yellow-50 border-yellow-300'
                : 'bg-green-50 border-green-300'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-xl text-gray-900">
                    Extraction Confidence: {(validationResults.confidence_score * 100).toFixed(1)}%
                  </h3>
                  <div className="text-sm text-gray-600 mt-1">
                    {validationResults.errors && validationResults.errors.length > 0 && (
                      <span className="text-red-600 font-medium">{validationResults.errors.length} error(s)</span>
                    )}
                    {validationResults.errors && validationResults.errors.length > 0 && validationResults.warnings && validationResults.warnings.length > 0 && (
                      <span className="mx-2">•</span>
                    )}
                    {validationResults.warnings && validationResults.warnings.length > 0 && (
                      <span className="text-yellow-700 font-medium">{validationResults.warnings.length} warning(s)</span>
                    )}
                    {(!validationResults.errors || validationResults.errors.length === 0) && (!validationResults.warnings || validationResults.warnings.length === 0) && (
                      <span className="text-green-600 font-medium">All validations passed</span>
                    )}
                  </div>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                  !validationResults.is_valid
                    ? 'bg-red-200 text-red-900'
                    : validationResults.warnings && validationResults.warnings.length > 0
                    ? 'bg-yellow-200 text-yellow-900'
                    : 'bg-green-200 text-green-900'
                }`}>
                  {!validationResults.is_valid ? '❌ Invalid' : validationResults.warnings && validationResults.warnings.length > 0 ? '⚠️ Has Warnings' : '✓ Valid'}
                </span>
              </div>

              {/* Inline Errors */}
              {validationResults.errors && validationResults.errors.length > 0 && (
                <div className="mt-4 bg-red-100 border border-red-300 rounded-lg p-3">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <h4 className="text-red-800 font-semibold text-sm">Errors ({validationResults.errors.length})</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-red-700">
                    {validationResults.errors.map((err, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{err}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Inline Warnings */}
              {validationResults.warnings && validationResults.warnings.length > 0 && (
                <div className="mt-4 bg-yellow-100 border border-yellow-300 rounded-lg p-3">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <h4 className="text-yellow-800 font-semibold text-sm">Warnings ({validationResults.warnings.length})</h4>
                  </div>
                  <ul className="space-y-1 text-sm text-yellow-700">
                    {validationResults.warnings.map((warn, i) => (
                      <li key={i} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{warn}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          {extractedData?.metadata && (
            <div className="card">
              <h3 className="font-medium text-lg mb-2">Scorecard Metadata</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <span className="ml-2 font-medium">{extractedData.metadata.name || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Total Points:</span>
                  <span className="ml-2 font-medium">{extractedData.total_points || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Extracted Questions */}
          <div className="card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-medium">
                Extracted Questions ({extractedData?.questions.length || 0})
              </h2>
              <button onClick={addQuestion} className="btn-outline text-sm">
                + Add Question
              </button>
            </div>

            <fieldset className="mb-4">
              <legend className="block text-sm font-semibold text-gray-800 mb-1.5">Scoring System</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {UPLOAD_SCORING_OPTIONS.map(option => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 border rounded-xl p-3 cursor-pointer transition-colors ${
                      scoringSystem === option.value
                        ? 'border-cloudza-300 bg-cloudza-50 text-cloudza-800'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="upload-scoring-system"
                      checked={scoringSystem === option.value}
                      onChange={() => chooseScoringSystem(option.value)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block text-sm font-semibold">{option.label}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="space-y-4">
              {extractedData?.questions.map((q, idx) => (
                <div key={q.clientKey} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-gray-700">Question {idx + 1}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => moveQuestion(idx, -1)}
                        disabled={idx === 0}
                        aria-label={`Move question ${idx + 1} up`}
                        title="Move up"
                        className="btn-outline !py-1 !px-2 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <FaArrowUp />
                      </button>
                      <button
                        onClick={() => moveQuestion(idx, 1)}
                        disabled={idx === extractedData.questions.length - 1}
                        aria-label={`Move question ${idx + 1} down`}
                        title="Move down"
                        className="btn-outline !py-1 !px-2 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <FaArrowDown />
                      </button>
                      <button
                        onClick={() => removeQuestion(idx)}
                        className="text-red-600 hover:text-red-800 text-sm ml-1"
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      value={q.questionName}
                      onChange={e => updateQuestion(idx, 'questionName', e.target.value)}
                      className="input-field"
                      placeholder="Question Name/ID"
                    />
                    {complianceOnly ? (
                      <p className="input-field bg-gray-50 text-gray-700">Compliance</p>
                    ) : (
                      <SelectField
                        value={q.type}
                        onChange={e => updateQuestion(idx, 'type', e.target.value)}
                        className="input-field"
                      >
                        {ASSESSMENT_TYPE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </SelectField>
                    )}
                  </div>

                  <textarea
                    rows="3"
                    value={q.description}
                    onChange={e => updateQuestion(idx, 'description', e.target.value)}
                    className="input-field w-full"
                    placeholder="Question description and criteria"
                  />

                  <textarea
                    rows="2"
                    value={q.expected || ''}
                    onChange={e => updateQuestion(idx, 'expected', e.target.value)}
                    className="input-field w-full"
                    placeholder="Expected answer or scoring guide"
                  />

                  <input
                    type="number"
                    step="0.1"
                    value={q.weight}
                    onChange={e => updateQuestion(idx, 'weight', parseFloat(e.target.value) || 0)}
                    className="input-field w-32"
                    placeholder="Weight"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <button
              onClick={() => setPreviewMode(false)}
              className="btn-outline"
            >
              ← Back to Upload
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : '✓ Confirm & Save'}
            </button>
          </div>
        </div>
      )}
      <ConfirmationModal
        open={confirmComplianceOnly}
        title="Switch to Compliance-only"
        message={`${nonComplianceCount} question${nonComplianceCount === 1 ? '' : 's'} will become Compliance questions and lose any QA weighting. A compliance-only scorecard has no QA score.`}
        confirmText="Convert to Compliance"
        onClose={() => setConfirmComplianceOnly(false)}
        onConfirm={() => {
          setConfirmComplianceOnly(false);
          applyComplianceOnly();
        }}
      />
    </div>
  );
};

export default TemplateUpload;
